import React, { useId } from 'react';

export interface PushButtonProps {
  isPressed: boolean;
  color?: string; // Base color, e.g. '#22aa22' or 'var(--color-primary)'
  label?: string; // Text on the button
  width?: string | number;
  height?: string | number;
}

export const PushButtonSvg: React.FC<PushButtonProps> = ({ 
  isPressed, 
  color = '#22aa22',
  label = '',
  width = '100%', 
  height = '100%' 
}) => {
  const uId = useId().replace(/:/g, '');
  
  const outerBaseId = `outerBase-${uId}`;
  const metallicRimId = `metallicRim-${uId}`;
  const buttonFaceId = `buttonFace-${uId}`;
  const buttonFacePressedId = `buttonFacePressed-${uId}`;
  const innerShadowId = `innerShadow-${uId}`;
  const dropShadowId = `dropShadow-${uId}`;

  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 120 120" 
      width={width} 
      height={height}
      style={{ overflow: 'visible', userSelect: 'none' }}
    >
      <defs>
        {/* Dış Siyah Gövde */}
        <radialGradient id={outerBaseId} cx="50%" cy="50%">
          <stop offset="85%" stopColor="#222"/>
          <stop offset="100%" stopColor="#0a0a0a"/>
        </radialGradient>

        {/* Metalik Çerçeve (Rim) */}
        <linearGradient id={metallicRimId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f5f5f5"/>
          <stop offset="25%" stopColor="#d5d5d5"/>
          <stop offset="50%" stopColor="#e5e5e5"/>
          <stop offset="75%" stopColor="#b5b5b5"/>
          <stop offset="100%" stopColor="#858585"/>
        </linearGradient>

        {/* Buton Yüzey Gradient (Normal - Hafif Çukur) */}
        {/* Çukur hissi için merkez biraz daha koyu, kenarlara doğru ana renk, en dış kenar yine hafif koyu */}
        <radialGradient id={buttonFaceId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000" stopOpacity="0.15" />
          <stop offset="40%" stopColor="#000" stopOpacity="0.05" />
          <stop offset="80%" stopColor="#000" stopOpacity="0" />
          <stop offset="95%" stopColor="#000" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.3" />
        </radialGradient>

        {/* Buton Yüzey Gradient (Basılı - Daha Koyu) */}
        <radialGradient id={buttonFacePressedId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000" stopOpacity="0.3" />
          <stop offset="40%" stopColor="#000" stopOpacity="0.25" />
          <stop offset="80%" stopColor="#000" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.4" />
        </radialGradient>

        {/* Çukur için İç Gölge (Inner Shadow) */}
        <radialGradient id={innerShadowId} cx="50%" cy="50%" r="50%">
          <stop offset="80%" stopColor="#000" stopOpacity="0" />
          <stop offset="95%" stopColor="#000" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.5" />
        </radialGradient>

        {/* Dış Gölge */}
        <filter id={dropShadowId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.4"/>
        </filter>
      </defs>

      {/* Dış Gölge & Siyah Gövde */}
      <circle cx="60" cy="60" r="56" fill={`url(#${outerBaseId})`} filter={`url(#${dropShadowId})`}/>

      {/* Metalik Çerçeve */}
      <circle cx="60" cy="60" r="48" fill={`url(#${metallicRimId})`} />
      
      {/* Metalik Çerçevenin İç Kısmındaki İnce Karanlık Çizgi */}
      <circle cx="60" cy="60" r="42.5" fill="none" stroke="#222" strokeWidth="1" />

      {/* Ana Grup (Basılma efekti transform) */}
      <g 
        style={{ 
          transform: isPressed ? 'scale(0.97)' : 'scale(1)', 
          transformOrigin: '60px 60px',
          transition: 'transform 0.08s ease'
        }}
      >
        {/* Buton Zemin Rengi */}
        <circle 
          cx="60" 
          cy="60" 
          r="42"
          fill={color}
          style={{ transition: 'fill 0.1s ease' }}
        />

        {/* Çukur ve Mat Dokusu */}
        <circle 
          cx="60" 
          cy="60" 
          r="42"
          fill={isPressed ? `url(#${buttonFacePressedId})` : `url(#${buttonFaceId})`}
          style={{ transition: 'fill 0.1s ease' }}
        />

        {/* İç Gölge Katmanı (Kenarları Karartma) */}
        <circle 
          cx="60" 
          cy="60" 
          r="42"
          fill={`url(#${innerShadowId})`}
          pointerEvents="none"
        />

        {/* Yazı */}
        {label && (
          <text 
            x="60" 
            y="62" 
            fontFamily="Arial, Helvetica, sans-serif"
            fontSize="16px"
            fontWeight="bold"
            fill="#ffffff"
            textAnchor="middle"
            dominantBaseline="central"
            pointerEvents="none"
            letterSpacing="1px"
            opacity={isPressed ? 0.8 : 1}
            style={{ textShadow: '0 1px 1px rgba(0,0,0,0.6)' }}
          >
            {label}
          </text>
        )}
      </g>
    </svg>
  );
};
